import React, { useContext, useEffect, useState } from 'react';
import imageCompression from 'browser-image-compression';
import { heicTo } from 'heic-to';
import ApiContext from 'api/context';
import { MAX_CHUNK_SIZE, MAX_VIDEO_SIZE, MAX_UPLOAD_IMAGES } from '../../../utils/upload-constants';

const compressImage = async (file: File): Promise<File> => {
  const options = { maxSizeMB: 1, maxWidthOrHeight: 1920, useWebWorker: true };
  return await imageCompression(file, options);
};

const generateVideoPoster = async (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);

    video.src = url;
    video.currentTime = 0.1; // Seek to a small time to get first frame

    video.addEventListener('loadeddata', () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (ctx) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        const posterUrl = canvas.toDataURL('image/jpeg', 0.8);
        URL.revokeObjectURL(url);
        resolve(posterUrl);
      } else {
        URL.revokeObjectURL(url);
        reject(new Error('Could not get canvas context'));
      }
    });

    video.addEventListener('error', () => {
      URL.revokeObjectURL(url);
      reject(new Error('Video loading failed'));
    });

    video.load();
  });
};

/**
 * One upload section: its slots, their upload progress and the handlers that fill them.
 * Called once per section — the overall garment photos and the close-ups of the damage each own
 * their own instance, so their slot counts and their `( n / 10 )` caps are independent.
 */
export default function useRepairUpload(maxUploads: number = MAX_UPLOAD_IMAGES, initialImages?: string[]) {
  const api = useContext(ApiContext);
  const [images, setImages] = useState<string[]>([]);
  const [seeded, setSeeded] = useState(false);

  // A garment already in the cart brings its photos with it, and the customer can add to them.
  // Seeded once, so removing one of them does not bring it straight back.
  useEffect(() => {
    if (!seeded && initialImages?.length) {
      setImages(initialImages);
      setSeeded(true);
    }
  }, [initialImages, seeded]);
  const [fileTypes, setFileTypes] = useState<{ [key: number]: string }>({});
  const [uploadProgress, setUploadProgress] = useState<{ [key: number]: number }>({});
  const [videoPosters, setVideoPosters] = useState<{ [key: number]: string }>({});

  const uploadToAws = async (file: File, fileIndex: number): Promise<string | null> => {
    try {
      const { uploadId, key } = await api.product.startUpload(file.name, file.type);
      const totalChunks = Math.ceil(file.size / MAX_CHUNK_SIZE);
      if (totalChunks === 1) {
        // Single chunk: simulate incremental progress until 95, then actual upload, then finishUpload sets 100.
        const simulated = [5, 20, 40, 60, 80, 90, 95];
        // Kick off real upload in parallel
        const chunk = file.slice(0, file.size);
        const uploadPromise = api.product.uploadChunk(uploadId, 1, key, chunk).catch((err) => {
          throw err;
        });
        for (const pct of simulated) {
          if ((uploadProgress[fileIndex] || 0) >= 96) break;
          await new Promise((r) => setTimeout(r, 70));
          setUploadProgress((prev) => (prev[fileIndex] && prev[fileIndex] > pct ? prev : { ...prev, [fileIndex]: pct }));
        }
        await uploadPromise;
      } else {
        for (let partNumber = 1; partNumber <= totalChunks; partNumber++) {
          const start = (partNumber - 1) * MAX_CHUNK_SIZE;
          const end = Math.min(start + MAX_CHUNK_SIZE, file.size);
          const chunk = file.slice(start, end);
          await api.product.uploadChunk(uploadId, partNumber, key, chunk);
          setUploadProgress((prev) => {
            const nextPct = Math.min(100, Math.round((partNumber / totalChunks) * 100));
            if (prev[fileIndex] === nextPct) return prev;
            return { ...prev, [fileIndex]: nextPct };
          });
        }
      }
      const { url } = await api.product.completeUpload(uploadId);
      return url;
    } catch (error) {
      console.error('Error uploading file to AWS:', error);
      return null;
    }
  };

  const finishUpload = (fileIndex: number, uploadResult: string | null) => {
    setUploadProgress((prev) => ({ ...prev, [fileIndex]: 100 }));
    setTimeout(() => {
      setImages((prev) => {
        const next = [...prev];
        next[fileIndex] = uploadResult || 'failed-upload';
        return next;
      });
    }, 120);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);

    if (files.length === 0) {
      return;
    }

    // Reset file input to allow selecting same files again
    e.target.value = '';

    // Check upload limit
    const availableSlots = maxUploads - images.length;
    if (availableSlots <= 0) {
      console.warn('Maximum upload limit reached');
      return;
    }

    // Limit files to available slots
    const filesToProcess = files.slice(0, availableSlots);

    try {
      const startIndex = images.length;
      // Reserve slots with empty strings instead of placeholder sentinels
      const emptySlots = filesToProcess.map(() => '');
      setImages((prevImages) => [...prevImages, ...emptySlots]);

      const newFileTypes = {};
      filesToProcess.forEach((file, index) => {
        const fileIndex = startIndex + index;
        newFileTypes[fileIndex] = file.type;
      });
      setFileTypes((prev) => ({ ...prev, ...newFileTypes }));

      const filePromises = filesToProcess.map(async (file, index) => {
        const fileIndex = startIndex + index;
        const mimeType = file.type.toLowerCase();
        const fileName = file.name.toLowerCase();
        setUploadProgress((prev) => ({ ...prev, [fileIndex]: 0 }));
        let uploadResult: string | null = null;

        // Convert HEIC to JPEG
        if (mimeType === 'image/heic' || fileName.endsWith('.heic') || mimeType === '') {
          try {
            const blob = await heicTo({ blob: file, type: 'image/jpeg' });
            const convertedFile = new File([blob as BlobPart], fileName.replace(/\.heic$/, '.jpeg'), { type: 'image/jpeg' });
            uploadResult = await uploadToAws(convertedFile, fileIndex);
          } catch (error) {
            console.error('Error converting HEIC to JPEG', error);
          }
        }
        // Compress Image
        else if (mimeType.startsWith('image/')) {
          try {
            const compressedFile = await compressImage(file);
            uploadResult = await uploadToAws(compressedFile, fileIndex);
          } catch (error) {
            console.error('Error compressing image', error);
          }
        }
        // Upload Video (Check Size)
        else if (mimeType.startsWith('video/')) {
          if (file.size > MAX_VIDEO_SIZE) {
            console.warn('File size exceeds the maximum allowed for videos');
            return null; // Don't upload oversized videos
          } else {
            // Generate poster for video preview
            try {
              const posterUrl = await generateVideoPoster(file);
              setVideoPosters((prev) => ({ ...prev, [fileIndex]: posterUrl }));
            } catch (error) {
              console.error('Error generating video poster', error);
            }
            uploadResult = await uploadToAws(file, fileIndex);
          }
        }

        finishUpload(fileIndex, uploadResult);
        return uploadResult;
      });

      await Promise.all(filePromises);
    } catch (error) {
      console.error('Error uploading files to AWS:', error);
    }
  };

  const removeImage = (indexToRemove: number) => {
    setImages((prevImages) => prevImages.filter((_, index) => index !== indexToRemove));

    // Helper function to shift indexes down
    const shiftIndexes = (obj: { [key: number]: any }) => {
      const newObj = { ...obj };
      delete newObj[indexToRemove];
      const updatedObj = {};
      Object.keys(newObj).forEach((key) => {
        const index = parseInt(key);
        if (index > indexToRemove) {
          updatedObj[index - 1] = newObj[index];
        } else {
          updatedObj[index] = newObj[index];
        }
      });
      return updatedObj;
    };

    setFileTypes((prev) => shiftIndexes(prev));
    setUploadProgress((prev) => shiftIndexes(prev));
    setVideoPosters((prev) => shiftIndexes(prev));
  };

  return {
    images,
    fileTypes,
    uploadProgress,
    videoPosters,
    maxUploads,
    handleFileChange,
    removeImage,
    // Slots that are still uploading hold '', failed ones hold the sentinel; neither may reach the cart.
    validImages: images.filter((img) => img && img !== 'failed-upload'),
  };
}

export type RepairUpload = ReturnType<typeof useRepairUpload>;
